import { Query, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type ReactNode, useEffect, useState } from 'react';

import { captureException } from '@/lib/sentry';

interface QueryProviderProps {
  children: ReactNode;
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      throwOnError: (error: Error, query: Query) => {
        captureException(error, {
          query: {
            key: query.queryKey,
            hash: query.queryHash,
            state: query.state,
          },
        });

        // Don't throw the error, let TanStack Query handle it
        return false;
      },
    },
    mutations: {
      throwOnError: (error: Error) => {
        // Capture the error with context
        captureException(error, {
          mutation: {
            error: error.message,
          },
        });

        // Don't throw the error, let TanStack Query handle it
        return false;
      },
    },
  },
});

export const QueryProvider = ({ children }: QueryProviderProps) => {
  const [isDevtoolsVisible, setIsDevtoolsVisible] = useState(
    localStorage.getItem('isDevtoolsVisible') === 'true' || false
  );
  useEffect(() => {
    if (window) {
      (window as any).toggleReactQueryDevtools = () => {
        setIsDevtoolsVisible(!isDevtoolsVisible);
        localStorage.setItem(
          'isDevtoolsVisible',
          (!isDevtoolsVisible).toString()
        );
      };
    }
  }, [isDevtoolsVisible]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && isDevtoolsVisible && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
};
